import React, { useState } from 'react';
import { useData } from '../../store/DataContext';
import { Button } from '../../components/ui/Button';
import { CheckCircleIcon, ClipboardListIcon, FileTextIcon, XCircleIcon } from 'lucide-react';
import { Encounter } from '../../types';
import { PrescriptionFormPreview } from './PrescriptionFormPreview';
type Props = {
  onBack?: () => void;
};
export function PharmacyDispensing({
  onBack
}: Props) {
  const {
    encounters,
    patients,
    currentUser,
    updateEncounter,
    dispenseMedicine
  } = useData();
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [statuses, setStatuses] = useState<Record<string, 'Dispensed' | 'Not Dispensed'>>({});
  const [message, setMessage] = useState('');
  const [showPrescriptionForm, setShowPrescriptionForm] = useState(false);
  const selected = selectedId ? encounters.find(encounter => encounter.id === selectedId) || null : null;
  const pending = encounters.filter(encounter => encounter.stage === 'PHARMACY');
  const patient = selected ? patients.find(item => item.id === selected.patientId) : undefined;
  const selectEncounter = (encounter: Encounter) => {
    setSelectedId(encounter.id);
    setStatuses(encounter.prescriptions.reduce<Record<string, 'Dispensed' | 'Not Dispensed'>>((nextStatuses, prescription) => {
      if (prescription.status !== 'Pending') {
        nextStatuses[prescription.id] = prescription.status;
      }
      return nextStatuses;
    }, {}));
    setMessage('');
    setShowPrescriptionForm(false);
  };
  const markDispensed = async (prescriptionId: string, drugName: string) => {
    if (!selected) return;
    const result = await dispenseMedicine({
      drugName,
      encounterId: selected.id,
      patientId: selected.patientId,
      prescriptionId
    });
    if (!result.ok) {
      setMessage(result.error || 'Unable to dispense this medicine.');
      return;
    }
    setStatuses(previous => ({
      ...previous,
      [prescriptionId]: 'Dispensed'
    }));
    setMessage('One unit was deducted using FEFO from an eligible unexpired batch and recorded in stock history.');
  };
  const markNotAvailable = (prescriptionId: string) => setStatuses(previous => ({
    ...previous,
    [prescriptionId]: 'Not Dispensed'
  }));
  const complete = () => {
    if (!selected || !currentUser) return;
    if (!selected.prescriptions.every(prescription => statuses[prescription.id])) {
      setMessage('Mark each prescription as Dispensed or Not Available before completing.');
      return;
    }
    updateEncounter(selected.id, {
      prescriptions: selected.prescriptions.map(prescription => {
        const status = statuses[prescription.id] || prescription.status;
        return {
          ...prescription,
          status,
          ...(status === 'Dispensed' ? {
            dispensedAt: new Date().toISOString(),
            dispensedBy: currentUser.id,
            pharmacistName: currentUser.fullName
          } : {})
        };
      }),
      stage: 'CLINICIAN'
    });
    setSelectedId(null);
    setStatuses({});
    setShowPrescriptionForm(false);
    setMessage('Dispensing recorded and patient file returned to the clinician.');
  };
  if (selected && patient) return <div className="space-y-5" data-id="element-5019" data-mp="2svse9rlgyb51">
        <div className="flex flex-col gap-3 border-b border-slate-200 pb-5 sm:flex-row sm:items-center sm:justify-between" data-id="element-5020" data-mp="2swu10tbukm2l">
          <div data-id="element-5021" data-mp="2nzv3s1wvzz06">
            <p className="text-sm font-semibold text-pink-600" data-id="element-5022" data-mp="32uhy3pwxe5nx">Dispensing</p>
            <h2 className="mt-1 text-xl font-bold text-slate-950" data-id="element-5023" data-mp="32sex6au8is4f">
              {patient.firstName} {patient.lastName}
            </h2>
            <p className="mt-1 text-sm text-slate-500" data-id="element-5024" data-mp="i3r7153gzrbx">
              {patient.bmcFileNumber} · One unit is deducted per item marked
              dispensed.
            </p>
          </div>
          <div className="flex flex-wrap gap-2" data-id="element-5025" data-mp="2iu9icfngbb2n">
            <Button variant="outline" onClick={() => {
          setSelectedId(null);
          setStatuses({});
          setMessage('');
          setShowPrescriptionForm(false);
        }} data-id="element-5026" data-mp="2ivkkkb190arn">
              Back to queue
            </Button>
            <Button variant="outline" onClick={() => setShowPrescriptionForm(true)} className="border-teal-200 text-teal-800 hover:bg-teal-50" data-id="element-5027" data-mp="2wremga3ltzv9">
              <FileTextIcon className="mr-2 h-4 w-4" data-id="element-5028" data-mp="36pkjoeeguhts" /> Generate prescription
              form
            </Button>
            <Button onClick={complete} className="bg-teal-600 hover:bg-teal-700" data-id="element-5029" data-mp="3fjza9d4g5es7">
              <CheckCircleIcon className="mr-2 h-4 w-4" data-id="element-5030" data-mp="3kjlmbj8jgozf" /> Complete dispensing
            </Button>
          </div>
        </div>
        {message && <p role="status" className="rounded-md bg-blue-50 px-3 py-2 text-sm text-blue-800" data-id="element-5031" data-mp="3pig7usxw4rn8">
            {message}
          </p>}
        <section className="overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm" data-id="element-5032" data-mp="3um8uqv3kzhu6">
          <div className="border-b border-slate-100 px-5 py-4" data-id="element-5033" data-mp="3pop6r3h2zqp1">
            <h3 className="font-bold text-slate-900" data-id="element-5034" data-mp="3lmu5fyz5hdb9">Prescribed medicines</h3>
            <p className="mt-1 text-sm text-slate-500" data-id="element-5035" data-mp="m77a7s6re63x">
              Dispense checks inventory by medicine or generic name. If no
              matching stock is found, use Not Available.
            </p>
          </div>
          <div className="divide-y divide-slate-100" data-id="element-5036" data-mp="cgvtf51z82fm">
            {selected.prescriptions.map(prescription => <article key={prescription.id} className="flex flex-col gap-4 px-5 py-4 lg:flex-row lg:items-center lg:justify-between" data-id="element-5037" data-mp="8gllhmumj1y4">
                <div data-id="element-5038" data-mp="106gf3vpildsl">
                  <h4 className="font-semibold text-slate-900" data-id="element-5039" data-mp="105x5d2a0pvpn">
                    {prescription.drugName}
                  </h4>
                  <p className="mt-1 text-sm text-slate-500" data-id="element-5040" data-mp="2uflsbeuhz7qa">
                    {prescription.dose} · {prescription.route} ·{' '}
                    {prescription.frequency} · {prescription.days} days
                  </p>
                </div>
                <div className="flex items-center gap-2" data-id="element-5041" data-mp="2pnlmj5rqxkls">
                  <span className={`mr-1 rounded-full px-2 py-1 text-xs font-semibold ${statuses[prescription.id] === 'Dispensed' ? 'bg-emerald-50 text-emerald-700' : statuses[prescription.id] === 'Not Dispensed' ? 'bg-red-50 text-red-700' : 'bg-slate-100 text-slate-600'}`} data-id="element-5042" data-mp="2pm0t5jh647pi">
                    {statuses[prescription.id] || 'Awaiting action'}
                  </span>
                  <Button size="sm" disabled={Boolean(statuses[prescription.id])} onClick={() => markDispensed(prescription.id, prescription.drugName)} className="bg-teal-600 hover:bg-teal-700" data-id="element-5043" data-mp="26r2ll3r3j30b">
                    <CheckCircleIcon className="mr-1.5 h-4 w-4" data-id="element-5044" data-mp="1khluwy4bjrc5" /> Dispense
                  </Button>
                  <Button size="sm" variant="outline" disabled={Boolean(statuses[prescription.id])} onClick={() => markNotAvailable(prescription.id)} className="border-red-200 text-red-700 hover:bg-red-50" data-id="element-5045" data-mp="1z608a2zd2y6y">
                    <XCircleIcon className="mr-1.5 h-4 w-4" data-id="element-5046" data-mp="2449ys0a55am8" /> Not available
                  </Button>
                </div>
              </article>)}
          </div>
        </section>
        {showPrescriptionForm && <PrescriptionFormPreview encounter={selected} patient={patient} pharmacist={currentUser} onClose={() => setShowPrescriptionForm(false)} data-id="element-5047" data-mp="2d3osr9pio3bh" />}
      </div>;
  return <div className="space-y-5" data-id="element-5048" data-mp="3t4gw0uxjk8fz">
      <div className="flex flex-col gap-3 border-b border-slate-200 pb-5 sm:flex-row sm:items-end sm:justify-between" data-id="element-5049" data-mp="3t3fblzumy9ll">
        <div data-id="element-5050" data-mp="1v1fv6dj2rj7">
          <p className="text-sm font-semibold text-pink-600" data-id="element-5051" data-mp="6o4g1xct8z8d">Pharmacy desk</p>
          <h2 className="mt-1 text-xl font-bold text-slate-950" data-id="element-5052" data-mp="6q79krd69ahu">
            Dispensing queue
          </h2>
          <p className="mt-1 text-sm text-slate-500" data-id="element-5053" data-mp="bp1wa4e9fj78">
            Patient prescriptions waiting for pharmacy action.
          </p>
        </div>
        {onBack && <Button variant="outline" onClick={onBack} data-id="element-5054" data-mp="3f4fwbit0j723">
            Back to overview
          </Button>}
      </div>
      <section className="overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm" data-id="element-5055" data-mp="3k53kzxtrrbt5">
        <div className="divide-y divide-slate-100" data-id="element-5056" data-mp="3p00wrcifnz9q">
          {pending.map(encounter => {
          const queuePatient = patients.find(item => item.id === encounter.patientId);
          if (!queuePatient) return null;
          return <article key={encounter.id} className="flex flex-col gap-4 px-5 py-4 sm:flex-row sm:items-center sm:justify-between" data-id="element-5057" data-mp="2ot43y7ke7fzm">
                <div className="flex items-center gap-3" data-id="element-5058" data-mp="2f2t0em4mf8t9">
                  <div className="rounded-lg bg-teal-50 p-2.5 text-teal-700" data-id="element-5059" data-mp="2a5anylreabgi">
                    <ClipboardListIcon className="h-5 w-5" data-id="element-5060" data-mp="2a36meagyxoje" />
                  </div>
                  <div data-id="element-5061" data-mp="25796u6dj4m7o">
                    <p className="font-semibold text-slate-900" data-id="element-5062" data-mp="214ch7vpknpu8">
                      {queuePatient.firstName} {queuePatient.lastName}
                    </p>
                    <p className="mt-0.5 text-sm text-slate-500" data-id="element-5063" data-mp="1w5rrwrbuyqkk">
                      {queuePatient.bmcFileNumber} ·{' '}
                      {encounter.prescriptions.length} prescribed items
                    </p>
                  </div>
                </div>
                <Button size="sm" onClick={() => selectEncounter(encounter)} className="bg-teal-600 hover:bg-teal-700" data-id="element-5064" data-mp="3ljo577rn7snk">
                  Review prescription
                </Button>
              </article>;
        })}
          {!pending.length && <div className="px-5 py-14 text-center" data-id="element-5065" data-mp="1ijawr3sujebj">
              <ClipboardListIcon className="mx-auto h-8 w-8 text-slate-300" data-id="element-5066" data-mp="1eiinwbnvzbfs" />
              <p className="mt-3 text-sm text-slate-500" data-id="element-5067" data-mp="1egeghjqs3uar">
                No prescriptions are waiting for pharmacy.
              </p>
            </div>}
        </div>
      </section>
    </div>;
}